/**
 * The screen shown when something the app did not anticipate goes wrong.
 *
 * There are two places a crash can surface: inside a route, where the router
 * catches it, and outside one, where the app-level error boundary does. Both
 * land here, so a failure reads the same either way and never puts a raw error
 * or a stack trace in front of the user (Principle IV, SC-006).
 *
 * @author John Grimes
 */

import { PlainError } from "./PlainError";
import { isApiError } from "../lib/api/apiClient";

import type { JSX } from "react";
import type { PlainError as PlainErrorBody } from "shared/api";

/** Props for {@link AppErrorScreen}. */
export type AppErrorScreenProps = {
  /** Whatever was thrown. */
  error: unknown;
};

/**
 * Describes a thrown value in plain language, keeping the technical content for
 * the disclosure.
 *
 * @param error - Whatever was thrown.
 * @returns The message to show and the detail to hide behind the disclosure.
 */
const describeCrash = (error: unknown): PlainErrorBody => {
  // A failure that already carries plain copy keeps it: the API client and the
  // plain-language mapping have already done this work.
  if (isApiError(error)) {
    return error.plain;
  }
  return {
    message:
      "Something went wrong and this screen could not be shown. Reloading the page usually sorts it out.",
    detail:
      error instanceof Error
        ? `${error.name}: ${error.message}\n\n${error.stack ?? ""}`.trim()
        : JSON.stringify(error),
  };
};

/**
 * Renders the failure, with a way out of it.
 *
 * @param props - The component's props.
 * @param props.error - Whatever was thrown.
 * @returns The error screen.
 */
export const AppErrorScreen = ({
  error,
}: Readonly<AppErrorScreenProps>): JSX.Element => (
  <div className="min-h-screen grid place-items-center bg-base-200 text-base-content p-4">
    <PlainError
      className="max-w-md"
      error={describeCrash(error)}
      actions={
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            location.reload();
          }}
        >
          Reload the page
        </button>
      }
    />
  </div>
);
